/**
 * Shared OTel `Resource` for every RUM signal (traces, logs, metrics).
 *
 * Parity story (epic "RUM parity"): the original SDK created its providers with
 * NO resource, so every signal landed under `unknown_service`. This builds a
 * single resource from the public init options + ambient browser facts, applied
 * identically to the trace / log / meter providers so all three carry the same
 * `service.name` and `session`-independent context.
 *
 * `telemetry.sdk.language: 'webjs'` marks the signal as browser-originated (the
 * backend uses it to tell RUM apart from server SDKs).
 */
import { resourceFromAttributes, type Resource } from '@opentelemetry/resources'
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions'

import type { ObservInitOptions } from './types.js'

/**
 * Build the resource attached to every provider. `service.name` is set only when
 * the host supplies one (otherwise the OTel default `unknown_service` stands, but
 * at least `telemetry.sdk.language` flags it as browser RUM). Browser facts are
 * read defensively so the SDK stays no-throw under SSR / no-`navigator`.
 */
export function buildResource(options: ObservInitOptions): Resource {
  const attrs: Record<string, string | boolean> = {
    'telemetry.sdk.language': 'webjs',
  }
  if (options.serviceName) attrs[ATTR_SERVICE_NAME] = options.serviceName
  if (options.serviceVersion) attrs[ATTR_SERVICE_VERSION] = options.serviceVersion
  if (options.environment) attrs['deployment.environment.name'] = options.environment

  try {
    if (typeof navigator !== 'undefined') {
      if (navigator.language) attrs['browser.language'] = navigator.language
      attrs['browser.mobile'] = /Mobi|Android/i.test(navigator.userAgent || '')
      if (navigator.userAgent) attrs['user_agent.original'] = navigator.userAgent
    }
  } catch {
    /* swallow — resource enrichment must never break init */
  }

  return resourceFromAttributes(attrs)
}
